<template>
  <div class="container-fluid classroom-setup py-4">
    <div class="row justify-content-center">
      <div class="col-lg-11">
        <!-- ============================ En-tête ============================ -->
        <div class="setup-head mb-4">
          <span class="badge bg-gradient-success text-xxs mb-2">
            {{ $t("classroom_setup.badge") }}
          </span>
          <h4 class="mb-1">{{ $t("classroom_setup.title") }}</h4>
          <p class="mb-0" style="opacity: 0.6; max-width: 720px">
            {{ $t("classroom_setup.subtitle") }}
          </p>
        </div>

        <!-- ============================ Modes ============================ -->
        <div class="row">
          <div class="col-lg-6 mb-4">
            <div
              class="card mode-card h-100"
              :class="{ selected: form.mode === modes.SCHOOL }"
              @click="form.mode = modes.SCHOOL"
            >
              <div class="card-body p-4">
                <div class="d-flex justify-content-between align-items-start">
                  <div class="icon icon-shape bg-gradient-info border-radius-2xl text-center">
                    <i class="fa fa-school text-lg opacity-10"></i>
                  </div>
                  <i
                    class="bi check"
                    :class="form.mode === modes.SCHOOL ? 'bi-check-circle-fill' : 'bi-circle'"
                  ></i>
                </div>
                <h6 class="mt-3 mb-1">{{ $t("classroom_setup.mode_school_title") }}</h6>
                <p class="text-sm mb-3" style="opacity: 0.65">
                  {{ $t("classroom_setup.mode_school_text") }}
                </p>
                <ul class="feature-list">
                  <li><i class="bi bi-check2"></i>{{ $t("classroom_setup.feat_add_student") }}</li>
                  <li><i class="bi bi-check2"></i>{{ $t("classroom_setup.feat_import") }}</li>
                  <li><i class="bi bi-check2"></i>{{ $t("classroom_setup.feat_payment_config") }}</li>
                  <li><i class="bi bi-check2"></i>{{ $t("classroom_setup.feat_finance") }}</li>
                </ul>
                <div class="sub-options-school mt-3">
                  <div class="d-flex justify-content-between align-items-center">
                    <div class="pe-3">
                      <p class="text-sm mb-0 font-weight-bold">{{ $t('classroom_setup.cert_rule_title') }}</p>
                      <p class="text-xs mb-0" style="opacity: 0.6">{{ $t('classroom_setup.cert_rule_text') }}</p>
                    </div>
                    <argon-switch :id="'opt-school-cert'" v-model="form.allow_certificate_for_all_school" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-lg-6 mb-4">
            <div
              class="card mode-card h-100"
              :class="{ selected: isCollaborative }"
              @click="form.mode = modes.COLLABORATIVE"
            >
              <div class="card-body p-4">
                <div class="d-flex justify-content-between align-items-start">
                  <div class="icon icon-shape bg-gradient-success border-radius-2xl text-center">
                    <i class="fa fa-people-arrows text-lg opacity-10"></i>
                  </div>
                  <i
                    class="bi check"
                    :class="isCollaborative ? 'bi-check-circle-fill' : 'bi-circle'"
                  ></i>
                </div>
                <h6 class="mt-3 mb-1">{{ $t("classroom_setup.mode_parent_title") }}</h6>
                <span class="badge bg-gradient-success text-xxs mb-2">ESKA</span>
                <p class="text-sm mb-3" style="opacity: 0.65">
                  {{ $t("classroom_setup.mode_parent_text") }}
                </p>

                <div class="sub-options" :class="{ disabled: !isCollaborative }">
                  <div
                    class="sub-option"
                    v-for="option in collaborativeOptions"
                    :key="option.key"
                  >
                    <div class="d-flex justify-content-between align-items-center">
                      <div class="pe-3">
                        <p class="text-sm mb-0 font-weight-bold">
                          {{ $t(`classroom_setup.${option.titleKey}`) }}
                        </p>
                        <p class="text-xs mb-0" style="opacity: 0.6">
                          {{ $t(`classroom_setup.${option.textKey}`) }}
                        </p>
                      </div>
                      <argon-switch
                        :id="`opt-${option.key}`"
                        v-model="form[option.key]"
                        :disabled="!isCollaborative"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ============================ Actions ============================ -->
        <div class="d-flex justify-content-end align-items-center gap-3 mt-4 mb-6">
          <argon-button
            color="success"
            variant="gradient"
            :loading="loading || fetching"
            style="height: 50px; width: 200px"
            @click="submit"
          >
            {{ $t("classroom_setup.save") }}
          </argon-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArgonButton from "@/components/ArgonButton";
import ArgonSwitch from "@/components/ArgonSwitch";
import {
  CLASSROOM_MODE,
  getClassroomSetup,
  loadModeFromApi,
  saveClassroomSetup,
} from "@/utils/classroomSetup";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";

export default {
  name: "classroom-setup",
  components: { ArgonButton, ArgonSwitch },
  data() {
    return {
      modes: CLASSROOM_MODE,
      collaborativeOptions: [
        {
          key: "allow_parent_student_creation",
          titleKey: "parent_add_title",
          textKey: "parent_add_text",
        },
        {
          key: "allow_parent_finance_configuration",
          titleKey: "parent_finance_title",
          textKey: "parent_finance_text",
        },
        {
          key: "allow_certificate_for_all",
          titleKey: "cert_rule_title",
          textKey: "cert_rule_text",
        },
      ],
      loading: false,
      /* Vrai tant que le mode du serveur n'est pas revenu. */
      fetching: true,
      form: {
        mode: CLASSROOM_MODE.SCHOOL,
        allow_parent_student_creation: true,
        allow_parent_finance_configuration: true,
        // switch pour le mode collaboratif (déjà existant)
        allow_certificate_for_all: true,
        // switch séparé pour le mode École
        allow_certificate_for_all_school: true,
      },
    };
  },
  computed: {
    isCollaborative() {
      return this.form.mode === CLASSROOM_MODE.COLLABORATIVE;
    },
  },
  async created() {
    // Cache local d'abord : l'écran s'affiche sans attendre le réseau.
    const setup = await getClassroomSetup();
    this.form.mode = setup.mode;
    this.form.allow_parent_student_creation =
      setup.allow_parent_student_creation === true || this.form.mode === this.modes.SCHOOL;
    this.form.allow_parent_finance_configuration =
      setup.allow_parent_finance_configuration === true || this.form.mode === this.modes.SCHOOL;
    this.form.allow_certificate_for_all =
      setup.allow_certificate_for_all === true;
    this.form.allow_certificate_for_all_school =
      setup.allow_certificate_for_all_school === true || this.form.mode === this.modes.SCHOOL;

    // Puis le mode enregistré côté serveur, qui fait autorité : c'est lui qui
    // permet de retrouver le choix de l'établissement depuis un autre poste.
    try {
      const remoteMode = await loadModeFromApi();
      if (remoteMode) this.form.mode = remoteMode;
    } finally {
      this.fetching = false;
    }
  },
  methods: {
    useDefaultMode() {
      this.form.mode = CLASSROOM_MODE.SCHOOL;
      this.form.allow_parent_student_creation = true;
      this.form.allow_parent_finance_configuration = true;
      this.submit();
    },
    async submit() {
      // Enregistrer avant le retour du serveur écraserait le mode réellement
      // en vigueur par celui affiché par défaut.
      if (this.loading || this.fetching) return;
      try {
        this.loading = true;
        await saveClassroomSetup({
          mode: this.form.mode,
          allow_parent_student_creation: this.form.allow_parent_student_creation,
          allow_parent_finance_configuration: this.form.allow_parent_finance_configuration,
          // Persiste séparément les options pour chaque mode
          allow_certificate_for_all: this.form.allow_certificate_for_all,
          allow_certificate_for_all_school: this.form.allow_certificate_for_all_school,
        });
        toast.success(this.$t("classroom_setup.saved"), { autoClose: 3000 });
        this.$router.push({ name: "classrooms" });
      } catch (error) {
        console.error("Failed to save classroom setup", error);
        toast.error(this.$t("classroom_setup.save_error"), { autoClose: 4000 });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.setup-head h4 {
  font-weight: 700;
}

.mode-card {
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.mode-card:hover {
  transform: translateY(-4px);
  border-color: rgba(46, 206, 191, 0.5);
}

.mode-card.selected {
  border-color: #2ecebf;
  box-shadow: 0 0 0 1px #2ecebf inset;
}

.mode-card .icon {
  width: 48px;
  height: 48px;
}

.mode-card .check {
  font-size: 20px;
  color: #2ecebf;
  opacity: 0.9;
}

.feature-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.feature-list li {
  font-size: 13px;
  opacity: 0.7;
  margin-bottom: 6px;
}

.feature-list li i {
  color: #2ecebf;
  margin-right: 8px;
}

.sub-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sub-options.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.sub-option {
  border: 1px dashed rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  padding: 14px;
  transition: opacity 0.2s ease;
}

.sub-option.disabled {
  opacity: 0.4;
  pointer-events: none;
}

.fade-slide-enter-active,
.fade-slide-leave-active,
.fade-slide-enter-from,
.fade-slide-leave-to {
  transition: none !important;
  opacity: 1 !important;
  transform: none !important;
}

</style>
